import { Component } from '@angular/core';
import { CreateQuizPayload } from '@edtech-ui/core/models/quiz-creation/create-quiz';
import { GetAllQuiz, GetAllQuizResponse, UpdateQuizNameTimePayload } from '@edtech-ui/core/models/quiz-review-interface.ts/get-all-quiz';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { DeleteDialogBoxComponent } from '../delete-dialog-box/delete-dialog-box.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { CourseStatusEnum } from '@edtech-ui/constants/course-status.enum';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { EditableStudyMaterialTypeEnum } from '@edtech-ui/constants/editable-material-type.enum';
import { StudyMaterialTypeEnum } from '@edtech-ui/constants/material-type.enum';
import { EditCourseTitleComponent } from '@edtech-ui/dashboard/manual-course-creation/edit-course-title/edit-course-title.component';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
	selector: 'app-classroom-quiz',
	standalone: true,
	imports: [ReactiveFormsModule,
		MatIconModule, MatMenuModule, CommonModule, MatButtonModule, MatTabsModule, MatProgressSpinnerModule, MatTooltipModule
	],
	templateUrl: './classroom-quiz.component.html',
	styleUrl: './classroom-quiz.component.scss'
})
export class ClassroomQuizComponent {

	_quizData: GetAllQuiz[] = [];
	_totalQuizCount: number = 0;
	classroomId: number = 0;
	quizId: number | string | undefined;
	subscription = new Subscription();
	StudyMaterialTypeEnum = StudyMaterialTypeEnum;
	EditableStudyMaterialTypeEnum = EditableStudyMaterialTypeEnum;
	filterOpen: boolean = false;
	selectedQuizFilter: CourseStatusEnum = CourseStatusEnum.All;
	_allQuizData: GetAllQuiz[] = [];
	courseStatuses = [CourseStatusEnum.All, CourseStatusEnum.Published, CourseStatusEnum.Draft];
	quizForm: FormGroup;
	CourseStatusEnum = CourseStatusEnum;
	isQuizLoading = false;
	isCreating = false;
	isModalOpen = false;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private dialog: MatDialog,
		private _toastr: ToastrService,
		private _quizService: QuizManagementService,
	) {
		this.quizForm = new FormGroup({
			quiz_name: new FormControl('', Validators.required),
			hours: new FormControl(null, [Validators.min(0)]),
			minutes: new FormControl(null, [Validators.min(0), Validators.max(60), Validators.maxLength(2)]),
			seconds: new FormControl(null, [Validators.min(0), Validators.max(60), Validators.maxLength(2)]),
		})

		this.subscription.add(this._quizService.reload$.pipe(tap(() => this.getAllQuizes())).subscribe());
	}

	ngOnInit(): void {
		this.route.parent?.paramMap.subscribe(params => {
			const classroomId = params.get('id');
			const quizId = params.get('quizId');
			if (classroomId) {
				this.classroomId = +classroomId;
			}
			if (quizId) { this.quizId = + quizId }
			this.getAllQuizes();
		});
	}

	toggleFilter() {
		this.filterOpen = !this.filterOpen;
	}

	goToQuizPage(quiz: any) {
		if (quiz.status === CourseStatusEnum.Draft) {
			this.router.navigate(['teacher-dashboard/quiz-management', this.classroomId, quiz.quiz_id]);
		} else {
			this.router.navigate(['teacher-dashboard/quiz-management', this.classroomId, quiz.quiz_id]);
		}
	}

	openEditTitleBox(materialEditId: string | number, materialEditTitle: string,
		materialEditType: EditableStudyMaterialTypeEnum) {
		const dialogRef = this.dialog.open(EditCourseTitleComponent, {
			width: '500px',
			data: {
				materialEditId, editTitle: materialEditTitle, label: materialEditType
			}
		});
		dialogRef.afterClosed().subscribe((updatedData: any) => {
			if (!updatedData) return;
			if (materialEditType === EditableStudyMaterialTypeEnum.Quiz) {
				const { quiz_name, time_limit } = updatedData;
				if (quiz_name.trim() !== materialEditTitle.trim()) {
					this.postUpdateQuizNameTime(materialEditId, quiz_name, time_limit);
				}
			}
		})
	}

	openDeleteDialogBox(materialId: string, materialType: StudyMaterialTypeEnum) {
		this.dialog.open(DeleteDialogBoxComponent, {
			width: '360px',
			data: { materialId, materialType }
		}).afterClosed().subscribe(result => {
			if (result) {
				if (materialType === StudyMaterialTypeEnum.Quiz) {
					this.deleteQuizesById(materialId);
				}
			}
		});
	}

	getAllQuizes() {
		if (this.classroomId === undefined || this.classroomId === null) {
			this._toastr.error('classroomId is missing');
			return;
		}
		this.isQuizLoading = true;
		this.subscription.add(
			this._quizService.getAllQuizes(this.classroomId).pipe(
				finalize(() => {
					this.isQuizLoading = false
				})).subscribe({
					next: (response: GetAllQuizResponse) => {
						if (response.Success && response.Data) {
							this._quizData = response.Data.data;
							this._allQuizData = response.Data.data;
							this.applyQuizFilter();
							this._totalQuizCount = response.Data.pagination?.totalQuizzes ?? 0;
						} else {
							this._toastr.error(response.Message);
						}
					},
					error: () => {
						this._toastr.error('Something went wrong while fetching quizzes');
					}
				})
		);
	}

	deleteQuizesById(quizId: string): void {
		this.subscription.add(
			this._quizService.deleteQuizById(quizId).subscribe({
				next: () => {
					this._toastr.success('Quiz deleted successfully');
					this.getAllQuizes();
				},
				error: () => {
					this._toastr.error('Failed to delete quiz');
				}
			})
		);
	}

	postUpdateQuizNameTime(quizId: number | string, quizName: string, timeLimit?: number) {

		const payload: UpdateQuizNameTimePayload = {
			quiz_name: quizName,
		}
		this.subscription.add(
			this._quizService.postUpdateQuizNameTime(quizId, payload).pipe(
				tap(response => {
					if (response.Success) {
						this._toastr.success('Quiz name update successfully')
					} else {
						this._toastr.error(response.Message)
					}
				}),
				catchError(() => {
					this._toastr.error('something went wrong while updating quiz name');
					return of(null);
				}),
			).subscribe()
		);
	}

	onFilterChange(status: CourseStatusEnum, type: StudyMaterialTypeEnum) {
		this.filterOpen = false;
		if (type === StudyMaterialTypeEnum.Quiz) {
			this.selectedQuizFilter = status;
			this.applyQuizFilter();
		}
	}

	applyQuizFilter() {
		if (this.selectedQuizFilter === CourseStatusEnum.All) {
			this._quizData = [...this._allQuizData];
		} else {
			this._quizData = this._allQuizData.filter(q => q.status === this.selectedQuizFilter);
		}
	}

	 createQuiz() {}
	// 	const quizName = this.quizForm.get('quiz_name')?.value;
	// 	if (!this.classroomId) {
	// 		this._toastr.error('Classroom ID is missing');
	// 		return;
	// 	}
	// 	this.isCreating = true;
	// 	const hours = this.quizForm.get('hours')?.value || 0;
	// 	const minutes = this.quizForm.get('minutes')?.value || 0;
	// 	const seconds = this.quizForm.get('seconds')?.value || 0;

	// 	const timeLimit = (hours * 3600) + (minutes * 60) + seconds;

	// 	if (timeLimit === 0) {
	// 		this._toastr.error('Please set a time limit');
	// 		this.isCreating = false;
	// 		return;
	// 	}

	// 	const quizPayload: CreateQuizPayload = {
	// 		quiz_name: quizName,
	// 		time_limit: timeLimit,
	// 	}
	// 	this.subscription.add(
	// 		this._quizService.postCreateQuiz(quizPayload, this.classroomId, quizName, timeLimit).pipe(
	// 			tap(response => {
	// 				if (response.Success) {
	// 					this.router.navigate(['/teacher-dashboard/create-quiz', this.classroomId, response.Data.quiz_id]);
	// 				} else {
	// 					this._toastr.error(response.Message);
	// 				}
	// 			}),
	// 			finalize(() => { this.isCreating = false }),
	// 			catchError(() => {
	// 				this.isCreating = false;
	// 				this._toastr.error('Something went wrong while creating the quiz. Please try again.');
	// 				return of(null);
	// 			})
	// 		).subscribe());
	// } comment this for now as , we are not using this function here and api paylaod got changes

	goToStudentQuizResultPage(quiz: any) {
		this.router.navigate(['teacher-dashboard/students-results'], {
			queryParams: { classroomId: this.classroomId, quizId: quiz.quiz_id }
		})
	}

	openModal() {
		this.isModalOpen = true;
	}

	closeModal() {
		this.isModalOpen = false;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}